import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { AuthorizationModule } from '@wilson/authorization';
import {
  FilterModule,
  QualificationV2CategoryFilterComponent,
  QualificationV2StatusFilterComponent,
} from '@wilson/filter';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

interface FilterValues {
  status: string[];
  category: string[];
  orgUnit: string[];
  userIds: string[];
  userLabels: string[];
}

@Component({
  selector: 'wilson-qualification-filter-drawer',
  templateUrl: './qualification-filter-drawer.component.html',
  styleUrls: ['./qualification-filter-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AuthorizationModule,
    FilterModule,
    NzButtonModule,
    NzDividerModule,
    NzDrawerModule,
    NzSelectModule,
    QualificationV2CategoryFilterComponent,
    QualificationV2StatusFilterComponent,
    TranslatePipe,
  ],
})
export class QualificationFilterDrawerComponent {
  selectedStatus: string[] = [];
  selectedCategories: string[] = [];
  selectedOrgUnits: string[] = [];
  selectedUserIds: string[] = [];
  selectedUserLabels: string[] = [];

  @Input() isDrawerVisible!: boolean;
  @Output() closeDrawer = new EventEmitter<void>();
  @Output() applyFilters = new EventEmitter<FilterValues>();

  private filterChangeSubject = new Subject<FilterValues>();

  constructor() {
    this.setupFilterChangeDetection();
  }

  private setupFilterChangeDetection(): void {
    this.filterChangeSubject
      .pipe(
        debounceTime(200),
        distinctUntilChanged((prev, curr) => this.areFiltersEqual(prev, curr)),
      )
      .subscribe((filters) => {
        this.applyFilters.emit(filters);
      });
  }

  onStatusChange(status: string[]): void {
    this.selectedStatus = status;
    this.emitFilterChanges();
  }

  onCategoryChange(categories: string[]): void {
    this.selectedCategories = categories;
    this.emitFilterChanges();
  }

  onOrgUnitChange(orgUnits: string[]): void {
    this.selectedOrgUnits = orgUnits;
    this.emitFilterChanges();
  }

  onUserIdsChange(userIds: string[]): void {
    this.selectedUserIds = userIds;
    this.emitFilterChanges();
  }

  onUserLabelsChange(userLabels: string[]): void {
    this.selectedUserLabels = userLabels;
    this.emitFilterChanges();
  }

  private emitFilterChanges(): void {
    this.filterChangeSubject.next({
      status: this.selectedStatus,
      category: this.selectedCategories,
      orgUnit: this.selectedOrgUnits,
      userIds: this.selectedUserIds,
      userLabels: this.selectedUserLabels,
    });
  }

  private areFiltersEqual(prev: FilterValues, curr: FilterValues): boolean {
    return JSON.stringify(prev) === JSON.stringify(curr);
  }
}
